import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, Eye, X, Save, Tag as TagIcon, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Editor } from '@tinymce/tinymce-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TINYMCE_KEY = process.env.REACT_APP_TINYMCE_KEY || 'no-api-key';

const emptyPost = {
  slug: '', title_en: '', title_zh: '', excerpt_en: '', excerpt_zh: '',
  body_html_en: '', body_html_zh: '', cover_image: '', author_name: 'ALIGCUS Team',
  author_avatar: '', tags: [], meta_title_en: '', meta_title_zh: '',
  meta_description_en: '', meta_description_zh: '', status: 'draft',
};

const BlogManager = ({ isZh = false, token }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, otherwise post object
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const editorEnRef = useRef(null);
  const editorZhRef = useRef(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog/posts?page_size=200`, { headers: authHeader });
      setPosts(res.data?.items || []);
    } catch {
      toast.error(isZh ? '加载文章失败' : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const startCreate = () => setEditing({ ...emptyPost });
  const startEdit = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/blog/posts/${id}`, { headers: authHeader });
      setEditing(r.data);
    } catch { toast.error(isZh ? '加载失败' : 'Load failed'); }
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (editing.status === 'published') {
      const need = ['title_en', 'title_zh', 'body_html_en', 'body_html_zh'];
      const missing = need.filter(k => !(editing[k] || '').trim());
      if (missing.length) {
        toast.error(isZh ? '发布需要中英双语全填' : 'Publishing requires both EN and ZH bilingual content');
        return;
      }
    }
    try {
      // Pull latest editor content (TinyMCE may not be flushed)
      const payload = {
        ...editing,
        body_html_en: editorEnRef.current?.getContent?.() ?? editing.body_html_en,
        body_html_zh: editorZhRef.current?.getContent?.() ?? editing.body_html_zh,
      };
      if (editing.id) {
        await axios.put(`${API}/admin/blog/posts/${editing.id}`, payload, { headers: authHeader });
        toast.success(isZh ? '已保存' : 'Saved');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { headers: authHeader });
        toast.success(isZh ? '已创建' : 'Created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isZh ? '保存失败' : 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isZh ? '确定删除？此操作不可逆。' : 'Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${id}`, { headers: authHeader });
      toast.success(isZh ? '已删除' : 'Deleted');
      load();
    } catch { toast.error(isZh ? '删除失败' : 'Delete failed'); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/upload`, fd, { headers: { ...authHeader, 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url || r.data?.path || '';
      if (url) {
        setEditing({ ...editing, cover_image: url });
        toast.success(isZh ? '封面上传成功' : 'Cover uploaded');
      }
    } catch { toast.error(isZh ? '上传失败' : 'Upload failed'); }
    finally { setUploading(false); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || (editing?.tags || []).includes(t)) return;
    setEditing({ ...editing, tags: [...(editing.tags || []), t] });
    setTagInput('');
  };
  const removeTag = (t) => setEditing({ ...editing, tags: (editing.tags || []).filter(x => x !== t) });

  // ---------- LIST VIEW ----------
  if (!editing) {
    return (
      <div data-testid="blog-manager">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">{isZh ? '博客文章' : 'Blog Posts'}</h2>
          <Button onClick={startCreate} className="bg-red-600 hover:bg-red-700 text-white" data-testid="blog-new-btn">
            <Plus className="w-4 h-4 mr-1" />{isZh ? '新建文章' : 'New Post'}
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />{isZh ? '加载中...' : 'Loading...'}</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">{isZh ? '还没有文章，点击新建开始撰写。' : 'No posts yet. Click "New Post" to begin.'}</div>
        ) : (
          <div className="grid gap-3">
            {posts.map(p => (
              <Card key={p.id} className="hover:shadow-md transition" data-testid={`blog-row-${p.slug}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt="" className="w-20 h-20 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 rounded bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0"><ImageIcon className="w-6 h-6" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.title_en || '—'}</div>
                    <div className="text-sm text-gray-600 truncate">{p.title_zh || '—'}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span className={`px-2 py-0.5 rounded-full ${p.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                      <span>· /{p.slug}</span>
                      {p.read_time_min ? <span>· {p.read_time_min} min</span> : null}
                      {(p.tags || []).slice(0, 3).map(t => <span key={t} className="text-red-600">#{t}</span>)}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {p.status === 'published' && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex">
                        <Button size="sm" variant="outline" data-testid={`blog-view-${p.slug}`}><Eye className="w-4 h-4" /></Button>
                      </a>
                    )}
                    <Button size="sm" variant="outline" onClick={() => startEdit(p.id)} data-testid={`blog-edit-${p.slug}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(p.id)} className="text-red-600 hover:bg-red-50" data-testid={`blog-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- EDIT VIEW ----------
  return (
    <div data-testid="blog-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{editing.id ? (isZh ? '编辑文章' : 'Edit Post') : (isZh ? '新建文章' : 'New Post')}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={cancel} data-testid="blog-cancel-btn"><X className="w-4 h-4 mr-1" />{isZh ? '取消' : 'Cancel'}</Button>
          <Button onClick={save} className="bg-red-600 hover:bg-red-700 text-white" data-testid="blog-save-btn"><Save className="w-4 h-4 mr-1" />{isZh ? '保存' : 'Save'}</Button>
        </div>
      </div>

      <Card><CardContent className="p-6 space-y-4">
        {/* Slug + Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">URL Slug</label>
            <Input value={editing.slug || ''} onChange={e => setEditing({ ...editing, slug: e.target.value })} placeholder="california-adu-law-2026" data-testid="blog-slug-input" />
            <div className="text-xs text-gray-500 mt-1">{isZh ? '留空将自动生成。最终 URL：/blog/{slug}' : 'Leave empty to auto-generate. Final URL: /blog/{slug}'}</div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '状态' : 'Status'}</label>
            <select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="blog-status-select">
              <option value="draft">{isZh ? '草稿 (不公开)' : 'Draft (private)'}</option>
              <option value="published">{isZh ? '发布 (要求中英双语全填)' : 'Published (requires both EN+ZH)'}</option>
            </select>
          </div>
        </div>

        {/* Cover image */}
        <div>
          <label className="text-sm font-medium block mb-1">{isZh ? '封面图' : 'Cover Image'}</label>
          {editing.cover_image && <img src={editing.cover_image} alt="cover" className="w-full max-w-md rounded mb-2" />}
          <div className="flex gap-2">
            <Input value={editing.cover_image || ''} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://..." data-testid="blog-cover-input" />
            <input type="file" accept="image/*" ref={fileInputRef} onChange={uploadCover} className="hidden" />
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="blog-cover-upload">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
              <span className="ml-1">{isZh ? '上传' : 'Upload'}</span>
            </Button>
          </div>
        </div>

        {/* Author */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '作者' : 'Author'}</label>
            <Input value={editing.author_name || ''} onChange={e => setEditing({ ...editing, author_name: e.target.value })} data-testid="blog-author-input" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">{isZh ? '作者头像 URL' : 'Author Avatar URL'}</label>
            <Input value={editing.author_avatar || ''} onChange={e => setEditing({ ...editing, author_avatar: e.target.value })} placeholder="https://..." />
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="text-sm font-medium block mb-1"><TagIcon className="w-3 h-3 inline mr-1" />{isZh ? '标签' : 'Tags'}</label>
          <div className="flex gap-2 mb-2">
            <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder={isZh ? '输入标签按回车' : 'Type tag and press Enter'} data-testid="blog-tag-input" />
            <Button type="button" variant="outline" onClick={addTag}>{isZh ? '添加' : 'Add'}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(editing.tags || []).map(t => (
              <span key={t} className="bg-red-50 text-red-700 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                #{t}<button onClick={() => removeTag(t)} className="hover:text-red-900"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <hr />

        {/* English content */}
        <div className="space-y-2 border-l-4 border-blue-500 pl-4">
          <h3 className="font-semibold text-blue-700">English Content</h3>
          <Input value={editing.title_en || ''} onChange={e => setEditing({ ...editing, title_en: e.target.value })} placeholder="Title (EN)" data-testid="blog-title-en" />
          <Textarea value={editing.excerpt_en || ''} onChange={e => setEditing({ ...editing, excerpt_en: e.target.value })} placeholder="Short excerpt (EN) — shown on list page" rows={2} />
          <Editor
            apiKey={TINYMCE_KEY}
            onInit={(_evt, editor) => { editorEnRef.current = editor; }}
            initialValue={editing.body_html_en || ''}
            init={{
              height: 400, menubar: false,
              plugins: 'lists link image media table code preview wordcount autolink',
              toolbar: 'undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | link image media table | code preview',
              branding: false, promotion: false,
              convert_urls: false, relative_urls: false, remove_script_host: false,
            }}
          />
          <Input value={editing.meta_title_en || ''} onChange={e => setEditing({ ...editing, meta_title_en: e.target.value })} placeholder="SEO meta title (EN) — optional, defaults to title" />
          <Textarea value={editing.meta_description_en || ''} onChange={e => setEditing({ ...editing, meta_description_en: e.target.value })} placeholder="SEO meta description (EN) — optional, 150-160 chars" rows={2} />
        </div>

        {/* Chinese content */}
        <div className="space-y-2 border-l-4 border-red-500 pl-4">
          <h3 className="font-semibold text-red-700">中文内容</h3>
          <Input value={editing.title_zh || ''} onChange={e => setEditing({ ...editing, title_zh: e.target.value })} placeholder="标题（中文）" data-testid="blog-title-zh" />
          <Textarea value={editing.excerpt_zh || ''} onChange={e => setEditing({ ...editing, excerpt_zh: e.target.value })} placeholder="文章摘要（中文） — 列表页显示" rows={2} />
          <Editor
            apiKey={TINYMCE_KEY}
            onInit={(_evt, editor) => { editorZhRef.current = editor; }}
            initialValue={editing.body_html_zh || ''}
            init={{
              height: 400, menubar: false,
              plugins: 'lists link image media table code preview wordcount autolink',
              toolbar: 'undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | link image media table | code preview',
              branding: false, promotion: false,
              convert_urls: false, relative_urls: false, remove_script_host: false,
            }}
          />
          <Input value={editing.meta_title_zh || ''} onChange={e => setEditing({ ...editing, meta_title_zh: e.target.value })} placeholder="SEO 标题（中文） — 可选，默认用文章标题" />
          <Textarea value={editing.meta_description_zh || ''} onChange={e => setEditing({ ...editing, meta_description_zh: e.target.value })} placeholder="SEO 描述（中文） — 可选，150-160 字" rows={2} />
        </div>
      </CardContent></Card>
    </div>
  );
};

export default BlogManager;
